/**
 * Staff Otto — keep the embedded widget's session token fresh.
 *
 * The dashboard mints a short-lived (per-bot) EdDSA session JWT for the
 * Otto widget in `AppShell` and bakes it into the iframe once at page
 * load. That token has a finite TTL (see `apps/server/src/routes/otto.ts`)
 * and the widget's iframe lives on the Otto host origin
 * (support.xopnetworks.com), which CANNOT re-mint it itself — the dashboard
 * bearer lives in the parent's localStorage and is not reachable
 * cross-origin. So a dashboard left open past the token's TTL used to
 * send an EXPIRED token on every message → the gateway answered
 * `401 "Invalid EdDSA token: Token expired"` → the widget showed
 * "Gateway HTTP 401" until the page was reloaded.
 *
 * The fix: the dashboard (which holds the bearer and can mint any time,
 * same-origin) pushes fresh tokens into the iframe via `postMessage`.
 * `chat.js` swaps its in-memory `chatToken` on an `otto:set-token`
 * message, so the conversation keeps working with no reload. Refresh
 * happens proactively (interval + when the tab regains focus) and
 * on-demand (chat.js asks with `otto:token-refresh-request` when the
 * gateway 401s an expired token, then replays the message once).
 *
 * These helpers are the testable seam; `useOttoTokenRefresh` wires them
 * into React in `AppShell`.
 */
import { api } from "../api.js";

/** Message the widget sends up to ask for a fresh token (gateway 401). */
export const OTTO_TOKEN_REFRESH_REQUEST = "otto:token-refresh-request" as const;
/** Message the dashboard posts down carrying a freshly-minted token. */
export const OTTO_SET_TOKEN = "otto:set-token" as const;

/**
 * The origin of the Otto host, derived from the embed base URL
 * (VITE_OTTO_BASE_URL, e.g. "https://support.xopnetworks.com/otto" → origin
 * "https://support.xopnetworks.com"). `postMessage` needs an ORIGIN target,
 * not the base-with-path. Returns null when the base is missing/invalid
 * (dev without the widget wired) so callers no-op cleanly.
 */
export function ottoOriginFromBase(baseUrl: string | undefined): string | null {
  if (!baseUrl) return null;
  try {
    return new URL(baseUrl).origin;
  } catch {
    return null;
  }
}

/** The Otto chat iframe embed.js injects into the page (title="Otto chat"). */
export function findOttoIframe(doc: Document = document): HTMLIFrameElement | null {
  return doc.querySelector<HTMLIFrameElement>('iframe[title="Otto chat"]');
}

/**
 * Push a token into the Otto iframe. Returns true if the iframe was
 * present and the message was posted. Best-effort: a missing iframe (the
 * widget hasn't mounted yet, or the user has no Otto access) is a no-op.
 */
export function postOttoToken(
  token: string,
  ottoOrigin: string,
  doc: Document = document,
): boolean {
  const win = findOttoIframe(doc)?.contentWindow;
  if (!win) return false;
  try {
    win.postMessage({ type: OTTO_SET_TOKEN, token }, ottoOrigin);
    return true;
  } catch {
    return false;
  }
}

/**
 * Mint a fresh Otto session token (same endpoint AppShell uses for the
 * initial injection) and hand it to the widget. Returns the token, or
 * null on any failure (never throws — a failed refresh must not break
 * the app; the widget's 401 self-heal will retry on the next message).
 */
export async function refreshOttoToken(
  ottoOrigin: string,
  doc: Document = document,
): Promise<string | null> {
  try {
    const { token } = await api<{ token: string }>("/api/me/otto-token");
    if (!token) return null;
    postOttoToken(token, ottoOrigin, doc);
    return token;
  } catch {
    return null;
  }
}
